import { CartAddEvent } from '@theme/events';
import { isSizeOption, requireSizeChoice } from '@theme/fablair-size-choice';

const HISTORY_KEY = 'fablair:recent-products:v1';
const HISTORY_EVENT = 'fablair:recent-change';
const validHandle = (value) => typeof value === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(value);
const readHistory = () => {
  try {
    const data = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    return Array.isArray(data) ? [...new Set(data.filter(validHandle))].slice(0, 12) : [];
  } catch { return []; }
};
const money = (value, element) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  try {
    return new Intl.NumberFormat(element.dataset.locale || document.documentElement.lang || 'tr', {
      style: 'currency', currency: element.dataset.currency || 'TRY',
    }).format(number / 100);
  } catch { return `${(number / 100).toFixed(2)} ${element.dataset.currency || 'TRY'}`; }
};
const rootPath = (element) => `${element.dataset.rootUrl || '/'}`.replace(/\/?$/, '/');
const cartSectionIds = () => [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
  .map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);

/** Ajax API sends rendered sections for Fabric's cart drawer and a real cart count. */
async function addItems(element, items, onSuccess) {
  const sections = cartSectionIds();
  let response;
  let payload;
  let failure;
  try {
    response = await fetch(element.dataset.cartAdd || `${rootPath(element)}cart/add.js`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
    });
    payload = await response.json();
    if (!response.ok || payload.status) {
      failure = typeof payload.description === 'string' ? payload.description : 'Ürün eklenemedi. Stok ve seçiminizi kontrol edin.';
    }
  } catch {
    failure = 'İşlemin sonucu doğrulanamadı. Tekrar eklemeden önce sepetinizi kontrol edin.';
  }

  // Shopify can adjust stock during a failed multi-item add; always reconcile the real cart.
  let cart;
  try {
    const cartResponse = await fetch(`${element.dataset.cartUrl || `${rootPath(element)}cart`}.js`, {
      credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store',
    });
    if (cartResponse.ok) cart = await cartResponse.json();
  } catch { /* The section response can still refresh the drawer. */ }

  if (!failure) onSuccess?.();
  if (cart || payload?.sections || !failure) {
    document.dispatchEvent(new CartAddEvent(cart, element.id || 'fablair-commerce', {
      source: cart ? 'fablair-commerce' : 'product-form-component',
      itemCount: cart ? cart.item_count : failure ? 0 : items.reduce((sum, item) => sum + item.quantity, 0),
      didError: Boolean(failure), sections: payload?.sections || {},
    }));
  }
  if (failure) throw new Error(failure);
}

class FablairTabs extends HTMLElement {
  connectedCallback() {
    this.controller?.abort(); this.resizeObserver?.disconnect();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.rails = [...this.querySelectorAll('[data-product-rail].fablair-commerce-grid--carousel')];
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (this.tabs.includes(tab)) this.activate(tab);
      const arrow = event.target.closest('[data-rail-step]');
      if (arrow) this.moveRail(document.getElementById(arrow.getAttribute('aria-controls')), Number(arrow.dataset.railStep));
    }, { signal });
    this.addEventListener('keydown', (event) => {
      const index = this.tabs.indexOf(event.target);
      if (index >= 0 && ['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1
          : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.activate(this.tabs[next]); this.tabs[next].focus(); return;
      }
      if (this.rails.includes(event.target) && ['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') event.target.scrollTo({left: event.key === 'Home' ? 0 : event.target.scrollWidth, behavior: this.scrollBehavior()});
        else this.moveRail(event.target, event.key === 'ArrowRight' ? 1 : -1);
      }
    }, { signal });
    this.rails.forEach(rail => rail.addEventListener('scroll', () => this.updateRail(rail), {signal, passive:true}));
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.rails.forEach(rail => this.updateRail(rail)));
      this.rails.forEach(rail => this.resizeObserver.observe(rail));
    }
    document.addEventListener('shopify:block:select', (event) => {
      const tab = this.tabs.find((item) => item === event.target || item.contains(event.target));
      if (tab) this.activate(tab);
    }, { signal });
    this.rails.forEach(rail => this.updateRail(rail));
  }
  scrollBehavior() { return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }
  moveRail(rail, direction) {
    if (!rail || !this.contains(rail)) return;
    const card = rail.firstElementChild;
    const width = card?.getBoundingClientRect().width || rail.clientWidth * .8;
    const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    rail.scrollBy({left: direction * (width + gap), behavior: this.scrollBehavior()});
  }
  updateRail(rail) {
    const panel = rail.closest('[role="tabpanel"]');
    if (!panel || panel.hidden) return;
    const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const position = Math.max(0, Math.min(maximum, rail.scrollLeft));
    const controls = panel.querySelector('[data-rail-controls]');
    if (controls) controls.hidden = maximum < 2;
    const progress = panel.querySelector('[data-rail-progress]');
    if (progress) {
      const fraction = rail.scrollWidth ? Math.min(1, rail.clientWidth / rail.scrollWidth) : 1;
      progress.style.width = `${fraction * 100}%`;
      progress.style.marginLeft = `${maximum ? (position / maximum) * (1 - fraction) * 100 : 0}%`;
    }
    const before = panel.querySelector('[data-rail-step="-1"]'), after = panel.querySelector('[data-rail-step="1"]');
    if (before) before.disabled = position < 2;
    if (after) after.disabled = position >= maximum - 2;
  }
  activate(tab) {
    this.tabs.forEach((item) => {
      const active = item === tab;
      item.setAttribute('aria-selected', String(active)); item.tabIndex = active ? 0 : -1;
      const panel = document.getElementById(item.getAttribute('aria-controls'));
      if (panel && this.contains(panel)) panel.hidden = !active;
    });
    this.rails.forEach(rail => this.updateRail(rail));
  }
  disconnectedCallback() { this.controller?.abort(); this.resizeObserver?.disconnect(); }
}

/** Progressive option controls keep the real variant select as the single source of truth. */
class RsVariantControls extends HTMLElement {
  connectedCallback() {
    this.controller?.abort(); this.controller = new AbortController();
    const signal = this.controller.signal;
    this.select = this.querySelector('[data-variant-select]');
    if (!this.select) return;
    this.variants = [...this.select.options].filter(option => option.value).map(option => {
      let values = []; try { values = JSON.parse(option.dataset.options || '[]'); } catch { /* Retain fallback. */ }
      return {option, values};
    });
    let names = []; try { names = JSON.parse(this.select.dataset.optionNames || '[]'); } catch { return; }
    if (!names.length || !this.variants.every(variant => variant.values.length === names.length)) return;
    this.colorIndex = Number(this.select.dataset.colorIndex ?? -1);
    this.sizeIndex = names.findIndex(isSizeOption);
    this.chosenSize = this.chosenSize || '';
    this.querySelector('[data-option-controls]')?.remove();
    const controls = document.createElement('div'); controls.dataset.optionControls = '';
    const order = names.map((_, index) => index).sort((a,b) => a === this.colorIndex ? -1 : b === this.colorIndex ? 1 : a-b);
    for (const index of order) {
      if (names[index] === 'Title' && this.variants.length === 1 && this.variants[0].values[index] === 'Default Title') continue;
      const field = document.createElement('fieldset'); field.className = 'rs-option-group';
      const optionLabel = isSizeOption(names[index]) ? (this.select.dataset.sizeLabel || (document.documentElement.lang.startsWith('tr') ? 'Beden' : names[index])) : names[index];
      const legend = document.createElement('legend'); const name = document.createElement('span'); name.textContent = optionLabel; const valueLabel = document.createElement('span'); valueLabel.dataset.optionSelected = String(index); valueLabel.className = 'rs-option-group__value'; legend.append(name, valueLabel); field.append(legend);
      const values = [...new Set(this.variants.map(variant => variant.values[index]))];
      if (this.dataset.mode === 'select') {
        const dropdown = document.createElement('select'); dropdown.dataset.optionSelect = String(index); dropdown.setAttribute('aria-label', optionLabel);
        const empty = document.createElement('option'); empty.value = ''; empty.textContent = 'Seçin'; dropdown.append(empty);
        for (const value of values) { const option = document.createElement('option'); option.value = value; option.textContent = value; dropdown.append(option); }
        field.append(dropdown); controls.append(field); continue;
      }
      for (const value of values) {
        const button = document.createElement('button'); button.type = 'button';
        button.dataset.optionIndex = String(index); button.dataset.optionValue = value;
        button.className = index === this.colorIndex ? 'rs-option rs-option--color' : 'rs-option';
        button.setAttribute('aria-label', value); button.title = value;
        const label = document.createElement('span'); label.textContent = value; button.append(label); field.append(button);
      }
      controls.append(field);
    }
    this.append(controls); this.select.hidden = true;
    const label = this.querySelector(`label[for="${CSS.escape(this.select.id)}"]`); if (label) label.hidden = true;
    this.addEventListener('click', event => {
      const button = event.target.closest('[data-option-index]');
      if (button && this.contains(button) && !button.disabled) this.choose(Number(button.dataset.optionIndex), button.dataset.optionValue, event.isTrusted);
    }, {signal});
    this.addEventListener('change', event => {
      if (!event.target.matches('[data-option-select]')) return;
      const index = Number(event.target.dataset.optionSelect);
      if (!event.target.value) {
        if (index === this.sizeIndex) this.chosenSize = '';
        this.refresh();
        return;
      }
      this.choose(index, event.target.value, true);
    }, {signal});
    this.select.addEventListener('change', (event) => {
      if (event.isTrusted && this.sizeIndex >= 0) {
        const variant = this.variants.find(item => item.option.value === this.select.value);
        this.chosenSize = variant?.values[this.sizeIndex] || '';
      }
      this.refresh();
    }, {signal});
    this.refresh();
  }
  choose(index, value, intentional = false) {
    const selected = this.variants.find(variant => variant.option.value === this.select.value);
    const candidates = this.variants.filter(variant => !variant.option.disabled && variant.values[index] === value);
    const candidate = candidates.sort((a,b) => {
      const matches = variant => variant.values.reduce((count, item, at) => count + Number(at !== index && item === selected?.values[at]), 0);
      return matches(b) - matches(a);
    })[0];
    if (!candidate) return;
    if (index === this.sizeIndex && intentional) this.chosenSize = value;
    this.select.value = candidate.option.value;
    this.select.dispatchEvent(new Event('change', {bubbles:true}));
  }
  hasSizeChoice() {
    if (this.sizeIndex < 0) return true;
    const selected = this.variants?.find(variant => variant.option.value === this.select?.value);
    return Boolean(this.chosenSize && selected?.values[this.sizeIndex] === this.chosenSize);
  }
  refresh() {
    if (!this.variants || !this.select) return;
    const selected = this.variants?.find(variant => variant.option.value === this.select.value);
    for (const label of this.querySelectorAll('[data-option-selected]')) {
      const index = Number(label.dataset.optionSelected);
      label.textContent = index === this.sizeIndex && !this.hasSizeChoice() ? 'Seçin' : selected?.values[index] || '';
    }
    for (const dropdown of this.querySelectorAll('[data-option-select]')) {
      const index = Number(dropdown.dataset.optionSelect);
      dropdown.value = index === this.sizeIndex && !this.hasSizeChoice() ? '' : selected?.values[index] || '';
      for (const option of dropdown.options) {
        if (!option.value) continue;
        option.disabled = !this.variants.some(variant => !variant.option.disabled && variant.values[index] === option.value &&
          (index === this.colorIndex || !selected || variant.values.every((item, at) => at === index || item === selected.values[at])));
      }
    }
    for (const button of this.querySelectorAll('[data-option-index]')) {
      const index = Number(button.dataset.optionIndex), value = button.dataset.optionValue;
      const possible = this.variants.some(variant => !variant.option.disabled && variant.values[index] === value &&
        (index === this.colorIndex || !selected || variant.values.every((item, at) => at === index || item === selected.values[at])));
      button.disabled = !possible;
      button.setAttribute('aria-pressed', String(selected?.values[index] === value && (index !== this.sizeIndex || this.hasSizeChoice())));
    }
  }
  disconnectedCallback() { this.controller?.abort(); }
}

class FablairCommerceQuick extends HTMLElement {
  connectedCallback() {
    this.controller?.abort(); this.controller = new AbortController();
    const signal = this.controller.signal;
    this.dialog = this.querySelector('dialog');
    this.select = this.querySelector('[data-variant-select]');
    this.quantity = this.querySelector('[data-quick-quantity]');
    this.media = [...this.querySelectorAll('[data-quick-media]')];
    this.motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    this.motionQuery?.addEventListener('change', () => this.syncCardPlayback(), {signal});
    document.addEventListener('visibilitychange', () => this.syncCardPlayback(), {signal});
    document.addEventListener('rs-quickview-state', () => this.syncCardPlayback(), {signal});
    this.dialog?.addEventListener('cancel', event => { event.preventDefault(); this.closeQuickView(); }, {signal});
    this.dialog?.addEventListener('keydown', event => {
      if (event.target.closest('input,select,textarea') || !event.target.closest('.fablair-commerce-dialog__gallery')) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.stepMedia(event.key === 'ArrowLeft' ? -1 : 1); }
    }, {signal});
    const stage = this.querySelector('[data-quick-stage]');
    stage?.addEventListener('pointerdown', event => { if (event.pointerType === 'touch' && !event.target.closest('video,iframe,model-viewer')) this.swipeStart = {x:event.clientX,y:event.clientY}; }, {signal,passive:true});
    stage?.addEventListener('pointerup', event => {
      if (!this.swipeStart) return;
      const x = event.clientX - this.swipeStart.x, y = event.clientY - this.swipeStart.y;
      this.swipeStart = null;
      if (Math.abs(x) > 40 && Math.abs(x) > Math.abs(y) * 1.4) this.stepMedia(x < 0 ? 1 : -1);
    }, {signal,passive:true});
    stage?.addEventListener('pointercancel', () => { this.swipeStart = null; }, {signal});
    if ('IntersectionObserver' in window) {
      this.cardObserver?.disconnect();
      this.cardObserver = new IntersectionObserver(entries => { this.cardVisible = entries[0]?.isIntersecting && entries[0].intersectionRatio >= .15; this.syncCardPlayback(); }, {threshold:[0,.15]});
      const media = this.querySelector('.fablair-commerce-card__media'); if (media) this.cardObserver.observe(media);
    }
    this.addEventListener('click', event => {
      const opener = event.target.closest('[data-quick-open]');
      if (opener && !opener.disabled) this.openQuickView(opener);
      if (event.target.closest('[data-quick-close]')) this.closeQuickView();
      const step = event.target.closest('[data-quick-step]'); if (step) this.stepMedia(Number(step.dataset.quickStep));
      if (event.target.closest('[data-card-motion-toggle]')) { this.motionPaused = this.cardVideo ? !this.cardVideo.paused : false; this.syncCardPlayback(true); }
      const thumb = event.target.closest('[data-quick-thumb]'); if (thumb) this.showMedia(thumb.dataset.quickThumb);
      const swatch = event.target.closest('[data-card-color]');
      if (swatch) this.querySelector('rs-variant-controls')?.choose?.(Number(this.dataset.colorIndex), swatch.dataset.cardColor);
      const adjust = event.target.closest('[data-quantity-change]');
      if (adjust && this.quantity) {
        if (Number(adjust.dataset.quantityChange) > 0) this.quantity.stepUp(); else this.quantity.stepDown();
        this.quantity.dispatchEvent(new Event('change', {bubbles:true}));
      }
      if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeQuickView();
      }
    }, {signal});
    this.dialog?.addEventListener('close', () => {
      clearTimeout(this.closeTimer); this.dialog.classList.remove('is-closing'); this.pauseMedia();
      if (!document.querySelector('.fablair-commerce-dialog[open]')) document.documentElement.classList.remove('rs-quickview-open');
      this.opener?.focus({preventScroll:true}); document.dispatchEvent(new Event('rs-quickview-state'));
    }, {signal});
    this.select?.addEventListener('change', () => this.updateVariant(), {signal});
    this.querySelector('[data-quick-form]')?.addEventListener('submit', async event => {
      event.preventDefault(); if (this.busy) return;
      const option = this.select?.selectedOptions[0], status = this.querySelector('[data-cart-status]');
      if (!requireSizeChoice(this, status)) return;
      if (!this.select?.value || option?.disabled) { status.textContent = 'Lütfen kullanılabilir bir renk ve beden seçin.'; this.querySelector('[data-option-index]:not(:disabled)')?.focus(); return; }
      if (this.quantity && !this.quantity.reportValidity()) return;
      const quantity = this.quantity ? Number(this.quantity.value) : Number(option.dataset.quantity) || 1;
      if (!Number.isInteger(quantity) || quantity < 1) return;
      const button = this.querySelector('[type="submit"]'); this.busy = true; button.disabled = true; status.textContent = 'Sepete ekleniyor…';
      try { await addItems(this, [{id:this.select.value, quantity}], () => this.closeQuickView(true)); status.textContent = 'Ürün sepetinize eklendi.'; }
      catch(error) { status.textContent = error.message; }
      finally { this.busy = false; button.disabled = !this.select.value || this.select.selectedOptions[0]?.disabled; }
    }, {signal});
    if (this.select && this.dataset.initialVariant) { this.select.value = this.dataset.initialVariant; this.select.dispatchEvent(new Event('change', {bubbles:true})); }
  }
  openQuickView(opener) {
    if (!this.dialog) return;
    this.opener = opener || this.querySelector('[data-quick-open]');
    clearTimeout(this.closeTimer); this.dialog.classList.remove('is-closing');
    if (!this.dialog.open) this.dialog.showModal();
    this.dialog.scrollTop = 0;
    document.documentElement.classList.add('rs-quickview-open');
    document.dispatchEvent(new Event('rs-quickview-state'));
    this.dialog.querySelector('[data-quick-close]')?.focus({preventScroll:true});
  }
  closeQuickView(immediate = false) {
    if (!this.dialog?.open) return;
    clearTimeout(this.closeTimer);
    if (immediate || this.motionQuery?.matches) { this.dialog.close(); return; }
    this.dialog.classList.add('is-closing');
    this.closeTimer = setTimeout(() => this.dialog?.close(), 260);
  }
  stepMedia(step) {
    const group = this.mediaFor(this.select?.selectedOptions[0]);
    if (group.length < 2) return;
    const current = group.findIndex(media => !media.hidden);
    const index = (Math.max(0,current) + step + group.length) % group.length;
    this.showMedia(group[index].dataset.quickMedia);
  }
  setupCardMotion(group) {
    const media = this.dataset.cardMotion === 'false' ? null : group.find(item => item.dataset.mediaType === 'video' && item.querySelector('video'));
    if (this.cardMotionId === media?.dataset.quickMedia && this.cardVideo) return;
    this.cardVideo?.pause(); this.cardVideo?.remove(); this.cardVideo = null;
    this.cardMotionId = media?.dataset.quickMedia;
    const toggle = this.querySelector('[data-card-motion-toggle]');
    if (toggle) toggle.hidden = !media;
    if (!media) return;
    const video = media.querySelector('video').cloneNode(true);
    video.removeAttribute('id'); video.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
    video.controls = false; video.autoplay = false; video.muted = true; video.defaultMuted = true; video.loop = true; video.playsInline = true; video.preload = 'none';
    video.setAttribute('muted',''); video.setAttribute('playsinline',''); video.setAttribute('aria-hidden','true'); video.setAttribute('tabindex','-1');
    video.removeAttribute('autoplay'); video.className = 'fablair-commerce-card__video';
    video.addEventListener('playing', () => video.classList.add('is-playing'), {signal:this.controller.signal});
    video.addEventListener('error', () => { video.classList.remove('is-playing'); if(toggle) toggle.hidden = true; }, {signal:this.controller.signal});
    this.querySelector('.fablair-commerce-card__image-link')?.append(video);
    this.cardVideo = video; this.motionPaused = false; this.syncCardPlayback();
  }
  syncCardPlayback(userInitiated = false) {
    if (!this.cardVideo) return;
    if (userInitiated) this.motionUserAllowed = true;
    const reduced = this.motionQuery?.matches && !this.motionUserAllowed;
    const play = this.cardVisible && !document.hidden && !document.querySelector('dialog[open]') && !document.documentElement.classList.contains('rs-quickview-open') && !this.motionPaused && !reduced;
    const toggle = this.querySelector('[data-card-motion-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-pressed', String(!play));
      toggle.setAttribute('aria-label', play ? 'Ürün videosunu duraklat' : 'Ürün videosunu oynat');
      const icon = toggle.querySelector('[data-motion-icon]'); if (icon) icon.textContent = play ? 'Ⅱ' : '▶';
    }
    if (play) this.cardVideo.play()?.catch(() => { this.cardVideo?.classList.remove('is-playing'); });
    else this.cardVideo.pause();
    if (reduced) this.cardVideo.classList.remove('is-playing');
  }
  pauseMedia() {
    this.dialog?.querySelectorAll('video').forEach(video => video.pause());
    this.dialog?.querySelectorAll('iframe').forEach(frame => {
      try { const origin = new URL(frame.src).origin; frame.contentWindow?.postMessage(JSON.stringify({event:'command',func:'pauseVideo',args:[]}), origin); frame.contentWindow?.postMessage({method:'pause'}, origin); } catch { /* Unsupported external player. */ }
    });
  }
  mediaFor(option) {
    const colorIndex = Number(this.dataset.colorIndex ?? -1);
    let colors = new Set();
    if (colorIndex >= 0) for (const candidate of this.select.options) {
      try { const values = JSON.parse(candidate.dataset.options || '[]'); if (values[colorIndex]) colors.add(values[colorIndex]); } catch { /* Ignore malformed metadata. */ }
    }
    // Split listings restrict the select to one color; image safety still depends on the whole product.
    const colorCount = Math.max(colors.size, Number(this.dataset.productColorCount) || 0);
    if (colorCount <= 1 && !this.media.some(item => item.dataset.variantAnchor === 'true')) return this.media;
    const start = this.media.findIndex(item => item.dataset.quickMedia === option?.dataset.mediaId);
    if (start < 0) return colorCount > 1 ? [] : this.media;
    let end = this.media.findIndex((item, index) => index > start && item.dataset.variantAnchor === 'true');
    if (end < 0) end = this.media.length;
    return this.media.slice(start, end);
  }
  showMedia(id) {
    this.pauseMedia();
    for (const media of this.media) media.hidden = media.dataset.quickMedia !== id;
    for (const thumb of this.querySelectorAll('[data-quick-thumb]')) thumb.setAttribute('aria-pressed', String(thumb.dataset.quickThumb === id));
    const group = this.mediaFor(this.select?.selectedOptions[0]), index = group.findIndex(media => media.dataset.quickMedia === id);
    const counter = this.querySelector('[data-quick-counter]'); if (counter) counter.textContent = group.length ? `${index + 1} / ${group.length}` : '';
    const progress = this.querySelector('[data-quick-progress]'); if (progress) progress.style.width = `${group.length ? ((index + 1) / group.length) * 100 : 0}%`;
    this.querySelectorAll('[data-quick-step]').forEach(button => { button.disabled = group.length < 2; });
  }
  updateVariant() {
    const option = this.select?.selectedOptions[0]; if (!option?.value) return;
    const price = this.querySelector('[data-selected-price]'); if (price) price.textContent = option.dataset.priceLabel;
    const cardPrice = this.querySelector('[data-card-price]'); if (cardPrice) cardPrice.textContent = option.dataset.priceLabel;
    for (const oldPrice of this.querySelectorAll('[data-card-compare], [data-quick-compare]')) { oldPrice.hidden = Number(option.dataset.compare) <= Number(option.dataset.price); oldPrice.textContent = option.dataset.compareLabel || ''; }
    const badge = this.querySelector('[data-card-badge]'); if (badge) { badge.hidden = !option.disabled && Number(option.dataset.compare) <= Number(option.dataset.price); badge.textContent = option.disabled ? 'Tükendi' : 'İndirim'; }
    const button = this.querySelector('[type="submit"]'); if (button) button.disabled = this.busy || option.disabled;
    if (this.quantity) {
      this.quantity.min = option.dataset.quantity || '1'; this.quantity.step = option.dataset.increment || '1';
      if (option.dataset.maximum) this.quantity.max = option.dataset.maximum; else this.quantity.removeAttribute('max');
      this.quantity.value = this.quantity.min;
    }
    const group = this.mediaFor(option), ids = new Set(group.map(media => media.dataset.quickMedia));
    for (const thumb of this.querySelectorAll('[data-quick-thumb]')) thumb.hidden = !ids.has(thumb.dataset.quickThumb);
    this.showMedia(group[0]?.dataset.quickMedia || '');
    this.setupCardMotion(group);
    const cardImage = this.querySelector('[data-card-image]');
    if (cardImage) { cardImage.hidden = !option.dataset.image; if (option.dataset.image) { cardImage.src = option.dataset.image; cardImage.removeAttribute('srcset'); } }
    let hover = this.querySelector('[data-card-hover]');
    const hoverSource = group.filter(media => media.dataset.mediaType === 'image')[1]?.querySelector('img');
    if (!hover && hoverSource && cardImage) { hover = cardImage.cloneNode(); hover.removeAttribute('data-card-image'); hover.dataset.cardHover = ''; hover.classList.add('fablair-commerce-card__image--hover'); hover.alt = ''; hover.setAttribute('aria-hidden','true'); cardImage.after(hover); }
    if (hover) { hover.hidden = !hoverSource; if (hoverSource) { hover.src = hoverSource.src; hover.removeAttribute('srcset'); } }
    for (const link of this.querySelectorAll('[data-card-link], [data-full-details]')) if (option.dataset.url) link.href = option.dataset.url;
    let values = []; try { values = JSON.parse(option.dataset.options || '[]'); } catch { /* No swatch selection. */ }
    for (const swatch of this.querySelectorAll('[data-card-color]')) swatch.setAttribute('aria-pressed', String(swatch.dataset.cardColor === values[Number(this.dataset.colorIndex)]));
    this.querySelector('rs-variant-controls')?.refresh?.();
  }
  disconnectedCallback() { clearTimeout(this.closeTimer); this.closeQuickView(true); this.controller?.abort(); this.cardObserver?.disconnect(); this.pauseMedia(); this.cardVideo?.pause(); }
}

class FablairLook extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.addEventListener('click', (event) => {
      const hotspot = event.target.closest('[data-look-target]');
      if (!hotspot) return;
      const item = document.getElementById(hotspot.dataset.lookTarget);
      if (!item || !this.contains(item)) return;
      item.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'nearest' });
      item.querySelector('[data-variant-select]')?.focus({ preventScroll: true });
    }, { signal });
    this.addEventListener('change', (event) => {
      const item = event.target.closest('[data-look-item]');
      if (!item) return;
      const select = item.querySelector('[data-variant-select]');
      const option = select.selectedOptions[0];
      if (event.target === select && select.value) {
        item.querySelector('[data-look-check]').checked = true;
        item.querySelector('[data-selected-price]').textContent = option.dataset.priceLabel;
      }
      this.updateTotal();
    }, { signal });
    this.querySelector('[data-look-form]')?.addEventListener('submit', (event) => this.submit(event), { signal });
    this.updateTotal();
  }
  updateTotal() {
    let total = 0;
    let validCount = 0;
    for (const item of this.querySelectorAll('[data-look-item]')) {
      const checked = item.querySelector('[data-look-check]').checked;
      const select = item.querySelector('[data-variant-select]');
      item.classList.toggle('is-selected', checked);
      if (checked && select.value && !select.selectedOptions[0].disabled) {
        total += Number(select.selectedOptions[0].dataset.price) * (Number(select.selectedOptions[0].dataset.quantity) || 1);
        validCount++;
      }
    }
    const output = this.querySelector('[data-look-total]');
    if (output) { output.hidden = !validCount; output.querySelector('[data-look-price]').textContent = money(total, this); }
  }
  async submit(event) {
    event.preventDefault();
    if (this.busy) return;
    const status = this.querySelector('[data-cart-status]');
    const items = [];
    for (const item of this.querySelectorAll('[data-look-item]')) {
      if (!item.querySelector('[data-look-check]').checked) continue;
      const select = item.querySelector('[data-variant-select]');
      if (!requireSizeChoice(item, status)) return;
      if (!select.value || select.selectedOptions[0].disabled) {
        status.textContent = 'İşaretlediğiniz her ürün için renk / beden seçin.';
        select.focus();
        select.reportValidity();
        return;
      }
      const id = select.value;
      const quantity = Number(select.selectedOptions[0].dataset.quantity) || 1;
      const duplicate = items.find((item) => item.id === id);
      if (duplicate) duplicate.quantity += quantity;
      else items.push({ id, quantity });
    }
    if (!items.length) { status.textContent = 'Sepete eklemek için en az bir ürün seçin.'; return; }
    const button = this.querySelector('[data-look-submit]');
    this.busy = true;
    button.disabled = true;
    status.textContent = 'Seçtikleriniz sepete ekleniyor…';
    try { await addItems(this, items); status.textContent = 'Seçtiğiniz ürünler sepetinize eklendi.'; }
    catch (error) { status.textContent = error.message; }
    finally { this.busy = false; button.disabled = false; }
  }
  disconnectedCallback() { this.controller?.abort(); }
}

class FablairRecentTracker extends HTMLElement {
  connectedCallback() {
    const handle = this.dataset.handle;
    if (!validHandle(handle)) return;
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify([handle, ...readHistory().filter((item) => item !== handle)].slice(0, 12)));
      window.dispatchEvent(new Event(HISTORY_EVENT));
    } catch { /* Private mode/storage restrictions must not block shopping. */ }
  }
}

class FablairRecent extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    window.addEventListener(HISTORY_EVENT, () => this.load(), { signal });
    window.addEventListener('storage', (event) => { if (event.key === HISTORY_KEY) this.load(); }, { signal });
    window.addEventListener('pageshow', (event) => { if (event.persisted) this.load(); }, { signal });
    this.load();
  }
  async load() {
    this.fetchController?.abort();
    this.fetchController = new AbortController();
    const signal = this.fetchController.signal;
    const handles = readHistory().filter((handle) => handle !== this.dataset.currentHandle).slice(0, Number(this.dataset.limit) || 6);
    const grid = this.querySelector('[data-recent-grid]');
    const status = this.querySelector('[data-recent-status]');
    if (!handles.length) { grid.replaceChildren(); this.hidden = !window.Shopify?.designMode; return; }
    const results = await Promise.allSettled(handles.map(async (handle) => {
      const response = await fetch(`${rootPath(this)}products/${encodeURIComponent(handle)}.js`, { signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const product = await response.json();
      if (!product.title || !validHandle(product.handle) || !Number.isFinite(Number(product.price))) throw new Error('Invalid product');
      return product;
    }));
    if (signal.aborted || !this.isConnected) return;
    const products = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
    grid.replaceChildren(...products.map((product) => this.card(product)));
    this.hidden = !products.length && !window.Shopify?.designMode;
    const empty = this.querySelector('[data-recent-empty]');
    if (empty) empty.hidden = products.length > 0;
    status.textContent = products.length && products.length < handles.length ? 'Bazı ürünler şu anda gösterilemiyor.' : '';
  }
  card(product) {
    const card = document.createElement('article');
    card.className = 'fablair-commerce-card';
    const link = document.createElement('a');
    link.href = `${rootPath(this)}products/${encodeURIComponent(product.handle)}`;
    link.className = 'fablair-commerce-card__image-link';
    const image = document.createElement('img');
    if (product.featured_image) {
      try {
        const source = new URL(product.featured_image, window.location.origin);
        if (source.protocol === 'https:' || source.protocol === 'http:') {
          source.searchParams.set('width', '600');
          image.src = source.href;
          image.alt = product.title;
          image.loading = 'lazy';
          image.width = 600;
          image.height = 800;
          image.className = 'fablair-commerce-card__image';
          link.append(image);
        }
      } catch { /* Keep the product name link usable if an image is unavailable. */ }
    }
    link.setAttribute('aria-label', product.title);
    const info = document.createElement('div');
    info.className = 'fablair-commerce-card__info';
    const title = document.createElement('a');
    title.className = 'fablair-commerce-card__title';
    title.href = link.href;
    title.textContent = product.title;
    const price = document.createElement('span');
    price.className = 'fablair-commerce-card__price';
    price.textContent = `${money(product.price, this)}${product.price_varies ? '’den başlayan' : ''}`;
    info.append(title, price);
    if (!product.available) { const sold = document.createElement('span'); sold.className = 'fablair-commerce-card__color'; sold.textContent = 'Tükendi'; info.append(sold); }
    card.append(link, info);
    return card;
  }
  disconnectedCallback() { this.controller?.abort(); this.fetchController?.abort(); }
}

for (const [name, implementation] of [
  ['rs-variant-controls', RsVariantControls], ['fablair-tabs', FablairTabs], ['fablair-commerce-quick', FablairCommerceQuick], ['fablair-look', FablairLook],
  ['fablair-recent-tracker', FablairRecentTracker], ['fablair-recent', FablairRecent],
]) if (!customElements.get(name)) customElements.define(name, implementation);

// Hero, look and campaign popovers can share server-rendered, color-safe quick views.
const openSharedQuickView = (targetId, opener) => {
  if (typeof targetId !== 'string') return;
  const target = document.getElementById(targetId.replace(/^#/, ''));
  const card = target?.matches('fablair-commerce-quick') ? target : target?.querySelector('fablair-commerce-quick');
  card?.openQuickView(opener);
};
document.addEventListener('click', event => {
  const opener = event.target.closest('[data-rs-quick-open]');
  if (!opener || opener.disabled) return;
  event.preventDefault(); openSharedQuickView(opener.dataset.rsQuickOpen, opener);
});
document.addEventListener('rs-quickview', event => openSharedQuickView(event.detail?.targetId, event.detail?.opener));

export { addItems, money };

